"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.VMClosureAnalyzer = void 0;
const inversify_1 = require("inversify");
const IVMCompilerUtils_1 = require("../../../interfaces/vm-transformers/IVMCompilerUtils");
const IVMVariableCollector_1 = require("../../../interfaces/vm-transformers/analyzers/IVMVariableCollector");
const ServiceIdentifiers_1 = require("../../../container/ServiceIdentifiers");
const NodeGuards_1 = require("../../../node/NodeGuards");
let VMClosureAnalyzer = class VMClosureAnalyzer {
    constructor(vmCompilerUtils, variableCollector) {
        this.vmCompilerUtils = vmCompilerUtils;
        this.variableCollector = variableCollector;
    }
    analyzeEnvironmentVariables(functionNode, context) {
        if (!functionNode.body) {
            return;
        }
        if (!NodeGuards_1.NodeGuards.isBlockStatementNode(functionNode.body)) {
            this.analyzeExpressionBodyClosures(functionNode, context);
            return;
        }
        const paramNames = this.variableCollector.collectParameterNames(functionNode);
        const declaredVars = this.variableCollector.collectDeclaredVariables(functionNode.body.body);
        const referencedInNested = this.findVariablesReferencedByNestedFunctions(functionNode.body.body);
        this.scanParametersForNestedFunctions(functionNode.params, referencedInNested);
        this.scanDefaultParameterFunctions(functionNode.params, paramNames, referencedInNested);
        this.markCapturedVariables(paramNames, declaredVars, referencedInNested, context);
    }
    scanNodeForIdentifiers(node, identifiers) {
        if (!node) {
            return;
        }
        if (NodeGuards_1.NodeGuards.isIdentifierNode(node)) {
            identifiers.add(node.name);
            return;
        }
        if (NodeGuards_1.NodeGuards.isFunctionExpressionNode(node) ||
            NodeGuards_1.NodeGuards.isArrowFunctionExpressionNode(node) ||
            NodeGuards_1.NodeGuards.isFunctionDeclarationNode(node)) {
            return;
        }
        if (NodeGuards_1.NodeGuards.isClassExpressionNode(node) || NodeGuards_1.NodeGuards.isClassDeclarationNode(node)) {
            return;
        }
        const nodeAsAny = node;
        for (const key of Object.keys(node)) {
            if (key === 'type' || key === 'parentNode') {
                continue;
            }
            const value = nodeAsAny[key];
            if (!value || typeof value !== 'object') {
                continue;
            }
            if (Array.isArray(value)) {
                for (const item of value) {
                    if (item && typeof item === 'object' && item.type) {
                        this.scanNodeForIdentifiers(item, identifiers);
                    }
                }
            }
            else if (value.type) {
                this.scanNodeForIdentifiers(value, identifiers);
            }
        }
    }
    analyzeExpressionBodyClosures(functionNode, context) {
        const paramNames = this.variableCollector.collectParameterNames(functionNode);
        const referencedInNested = new Set();
        this.scanNodeForNestedFunctions(functionNode.body, referencedInNested);
        this.scanParametersForNestedFunctions(functionNode.params, referencedInNested);
        this.scanDefaultParameterFunctions(functionNode.params, paramNames, referencedInNested);
        for (const paramName of paramNames) {
            if (referencedInNested.has(paramName)) {
                context.envVars.add(paramName);
            }
        }
    }
    scanPatternForNestedFunctions(pattern, referencedInNested) {
        if (NodeGuards_1.NodeGuards.isArrayPatternNode(pattern)) {
            for (const element of pattern.elements) {
                if (element) {
                    if (NodeGuards_1.NodeGuards.isAssignmentPatternNode(element)) {
                        this.scanNodeForNestedFunctions(element.right, referencedInNested);
                        this.scanPatternForNestedFunctions(element.left, referencedInNested);
                    }
                    else if (NodeGuards_1.NodeGuards.isRestElementNode(element)) {
                        this.scanPatternForNestedFunctions(element.argument, referencedInNested);
                    }
                    else {
                        this.scanPatternForNestedFunctions(element, referencedInNested);
                    }
                }
            }
        }
        else if (NodeGuards_1.NodeGuards.isObjectPatternNode(pattern)) {
            for (const property of pattern.properties) {
                if (NodeGuards_1.NodeGuards.isRestElementNode(property)) {
                    this.scanPatternForNestedFunctions(property.argument, referencedInNested);
                }
                else if (NodeGuards_1.NodeGuards.isPropertyNode(property)) {
                    if (NodeGuards_1.NodeGuards.isAssignmentPatternNode(property.value)) {
                        this.scanNodeForNestedFunctions(property.value.right, referencedInNested);
                        this.scanPatternForNestedFunctions(property.value.left, referencedInNested);
                    }
                    else {
                        this.scanPatternForNestedFunctions(property.value, referencedInNested);
                    }
                }
            }
        }
        else if (NodeGuards_1.NodeGuards.isAssignmentPatternNode(pattern)) {
            this.scanNodeForNestedFunctions(pattern.right, referencedInNested);
            this.scanPatternForNestedFunctions(pattern.left, referencedInNested);
        }
        else if (NodeGuards_1.NodeGuards.isRestElementNode(pattern)) {
            this.scanPatternForNestedFunctions(pattern.argument, referencedInNested);
        }
    }
    findVariablesReferencedByNestedFunctions(statements) {
        const referencedInNested = new Set();
        this.vmCompilerUtils.traverseStatements(statements, (stmt) => {
            if (NodeGuards_1.NodeGuards.isFunctionDeclarationNode(stmt)) {
                this.scanNestedFunctionForReferences(stmt, referencedInNested);
            }
            else if (NodeGuards_1.NodeGuards.isClassDeclarationNode(stmt)) {
                this.scanClassForReferences(stmt, referencedInNested);
            }
            else if (NodeGuards_1.NodeGuards.isVariableDeclarationNode(stmt)) {
                for (const decl of stmt.declarations) {
                    if (decl.init) {
                        this.scanNodeForNestedFunctions(decl.init, referencedInNested);
                    }
                    if (NodeGuards_1.NodeGuards.isArrayPatternNode(decl.id) || NodeGuards_1.NodeGuards.isObjectPatternNode(decl.id)) {
                        this.scanPatternForNestedFunctions(decl.id, referencedInNested);
                    }
                }
            }
            else if (NodeGuards_1.NodeGuards.isReturnStatementNode(stmt) && stmt.argument) {
                this.scanNodeForNestedFunctions(stmt.argument, referencedInNested);
            }
            else if (stmt.type === 'ThrowStatement') {
                const throwStmt = stmt;
                if (throwStmt.argument) {
                    this.scanNodeForNestedFunctions(throwStmt.argument, referencedInNested);
                }
            }
            else if (NodeGuards_1.NodeGuards.isExpressionStatementNode(stmt)) {
                this.scanNodeForNestedFunctions(stmt.expression, referencedInNested);
            }
            else if (NodeGuards_1.NodeGuards.isIfStatementNode(stmt)) {
                this.scanNodeForNestedFunctions(stmt.test, referencedInNested);
            }
            else if (NodeGuards_1.NodeGuards.isWhileStatementNode(stmt)) {
                this.scanNodeForNestedFunctions(stmt.test, referencedInNested);
            }
            else if (NodeGuards_1.NodeGuards.isDoWhileStatementNode(stmt)) {
                this.scanNodeForNestedFunctions(stmt.test, referencedInNested);
            }
            else if (NodeGuards_1.NodeGuards.isForStatementNode(stmt)) {
                if (stmt.init) {
                    this.scanNodeForNestedFunctions(stmt.init, referencedInNested);
                }
                if (stmt.test) {
                    this.scanNodeForNestedFunctions(stmt.test, referencedInNested);
                }
                if (stmt.update) {
                    this.scanNodeForNestedFunctions(stmt.update, referencedInNested);
                }
            }
        });
        return referencedInNested;
    }
    markCapturedVariables(paramNames, declaredVars, referencedInNested, context) {
        declaredVars.forEach((varName) => {
            if (referencedInNested.has(varName)) {
                context.envVars.add(varName);
            }
        });
        paramNames.forEach((paramName) => {
            if (referencedInNested.has(paramName)) {
                context.envVars.add(paramName);
            }
        });
    }
    scanParametersForNestedFunctions(params, referencedInNested) {
        if (!params) {
            return;
        }
        for (const param of params) {
            if (!param) {
                continue;
            }
            if (NodeGuards_1.NodeGuards.isAssignmentPatternNode(param)) {
                this.scanNodeForIdentifiers(param.right, referencedInNested);
                if (NodeGuards_1.NodeGuards.isArrayPatternNode(param.left) || NodeGuards_1.NodeGuards.isObjectPatternNode(param.left)) {
                    this.scanPatternForNestedFunctions(param.left, referencedInNested);
                }
            }
            else if (NodeGuards_1.NodeGuards.isArrayPatternNode(param) || NodeGuards_1.NodeGuards.isObjectPatternNode(param)) {
                this.scanPatternForNestedFunctions(param, referencedInNested);
            }
        }
    }
    scanDefaultParameterFunctions(params, paramNames, referencedInNested) {
        if (!params) {
            return;
        }
        for (let i = 0; i < params.length; i++) {
            const param = params[i];
            if (!param) {
                continue;
            }
            if (NodeGuards_1.NodeGuards.isAssignmentPatternNode(param)) {
                const availableParams = new Set();
                for (let j = 0; j < i; j++) {
                    const prevParam = params[j];
                    if (prevParam) {
                        this.variableCollector.collectFromPattern(prevParam, availableParams);
                    }
                }
                this.scanForDirectFunctionReferences(param.right, availableParams, paramNames, referencedInNested);
            }
        }
    }
    scanForDirectFunctionReferences(node, availableParams, allParams, referencedInNested) {
        if (!node) {
            return;
        }
        if (NodeGuards_1.NodeGuards.isFunctionExpressionNode(node) ||
            NodeGuards_1.NodeGuards.isArrowFunctionExpressionNode(node) ||
            NodeGuards_1.NodeGuards.isFunctionDeclarationNode(node)) {
            const referencedIdentifiers = new Set();
            this.scanNestedFunctionForReferences(node, referencedIdentifiers);
            const nestedDeclaredVars = new Set();
            if (node.params) {
                for (const param of node.params) {
                    const paramNamesList = this.vmCompilerUtils.extractIdentifiersFromPattern(param);
                    paramNamesList.forEach((name) => nestedDeclaredVars.add(name));
                }
            }
            if (NodeGuards_1.NodeGuards.isBlockStatementNode(node.body)) {
                const localVars = this.variableCollector.collectDeclaredVariables(node.body.body);
                for (const varName of localVars) {
                    nestedDeclaredVars.add(varName);
                }
            }
            for (const identifier of referencedIdentifiers) {
                if (allParams.has(identifier)) {
                    if (!nestedDeclaredVars.has(identifier)) {
                        referencedInNested.add(identifier);
                    }
                }
            }
            return;
        }
        const nodeAsAny = node;
        for (const key of Object.keys(node)) {
            if (key === 'type' || key === 'parentNode') {
                continue;
            }
            const value = nodeAsAny[key];
            if (!value || typeof value !== 'object') {
                continue;
            }
            if (Array.isArray(value)) {
                for (const item of value) {
                    if (item && typeof item === 'object' && item.type) {
                        this.scanForDirectFunctionReferences(item, availableParams, allParams, referencedInNested);
                    }
                }
            }
            else if (value.type) {
                this.scanForDirectFunctionReferences(value, availableParams, allParams, referencedInNested);
            }
        }
    }
    scanNodeForNestedFunctions(node, referencedInNested) {
        if (!node) {
            return;
        }
        if (NodeGuards_1.NodeGuards.isFunctionExpressionNode(node) ||
            NodeGuards_1.NodeGuards.isArrowFunctionExpressionNode(node) ||
            NodeGuards_1.NodeGuards.isFunctionDeclarationNode(node)) {
            this.scanNestedFunctionForReferences(node, referencedInNested);
            return;
        }
        if (NodeGuards_1.NodeGuards.isClassExpressionNode(node) || NodeGuards_1.NodeGuards.isClassDeclarationNode(node)) {
            this.scanClassForReferences(node, referencedInNested);
            return;
        }
        const nodeAsAny = node;
        for (const key of Object.keys(node)) {
            if (key === 'type' || key === 'parentNode') {
                continue;
            }
            const value = nodeAsAny[key];
            if (!value || typeof value !== 'object') {
                continue;
            }
            if (Array.isArray(value)) {
                for (const item of value) {
                    if (item && typeof item === 'object' && item.type) {
                        this.scanNodeForNestedFunctions(item, referencedInNested);
                    }
                }
            }
            else if (value.type) {
                this.scanNodeForNestedFunctions(value, referencedInNested);
            }
        }
    }
    scanNestedFunctionForReferences(func, referencedInNested) {
        if (!func.body) {
            return;
        }
        const allReferencedIdentifiers = new Set();
        if (func.params) {
            for (const param of func.params) {
                if (param && NodeGuards_1.NodeGuards.isAssignmentPatternNode(param)) {
                    this.scanNodeForIdentifiers(param.right, allReferencedIdentifiers);
                }
            }
        }
        if (!NodeGuards_1.NodeGuards.isBlockStatementNode(func.body)) {
            this.scanNodeForIdentifiers(func.body, allReferencedIdentifiers);
            const nestedFunctionReferences = new Set();
            this.scanNodeForNestedFunctions(func.body, nestedFunctionReferences);
            for (const varName of nestedFunctionReferences) {
                allReferencedIdentifiers.add(varName);
            }
        }
        else {
            this.scanStatementsForIdentifiers(func.body.body, allReferencedIdentifiers);
            const nestedFunctionReferences = new Set();
            this.scanNodeForNestedFunctions(func.body, nestedFunctionReferences);
            for (const varName of nestedFunctionReferences) {
                allReferencedIdentifiers.add(varName);
            }
        }
        const declaredInNested = this.variableCollector.collectParameterNames(func);
        if (NodeGuards_1.NodeGuards.isBlockStatementNode(func.body)) {
            const localVars = this.variableCollector.collectFunctionLevelDeclaredVariables(func.body.body);
            for (const varName of localVars) {
                declaredInNested.add(varName);
            }
        }
        allReferencedIdentifiers.forEach((identifier) => {
            if (!declaredInNested.has(identifier)) {
                referencedInNested.add(identifier);
            }
        });
    }
    scanClassForReferences(classNode, referencedInNested) {
        var _a;
        if (!((_a = classNode.body) === null || _a === void 0 ? void 0 : _a.body)) {
            return;
        }
        for (const member of classNode.body.body) {
            if (member.type === 'MethodDefinition' &&
                member.value &&
                NodeGuards_1.NodeGuards.isFunctionExpressionNode(member.value)) {
                this.scanNestedFunctionForReferences(member.value, referencedInNested);
            }
            else if (NodeGuards_1.NodeGuards.isPropertyDefinitionNode(member)) {
                if (member.value) {
                    this.scanNodeForIdentifiers(member.value, referencedInNested);
                    this.scanNodeForNestedFunctions(member.value, referencedInNested);
                }
            }
            else if (NodeGuards_1.NodeGuards.isStaticBlockNode(member)) {
                this.scanStatementsForIdentifiers(member.body, referencedInNested);
                for (const stmt of member.body) {
                    this.scanNodeForNestedFunctions(stmt, referencedInNested);
                }
            }
        }
    }
    scanStatementsForIdentifiers(statements, identifiers) {
        this.vmCompilerUtils.traverseStatements(statements, (stmt) => {
            if (NodeGuards_1.NodeGuards.isExpressionStatementNode(stmt)) {
                this.scanNodeForIdentifiers(stmt.expression, identifiers);
            }
            else if (NodeGuards_1.NodeGuards.isReturnStatementNode(stmt) && stmt.argument) {
                this.scanNodeForIdentifiers(stmt.argument, identifiers);
            }
            else if (NodeGuards_1.NodeGuards.isIfStatementNode(stmt)) {
                this.scanNodeForIdentifiers(stmt.test, identifiers);
            }
            else if (NodeGuards_1.NodeGuards.isVariableDeclarationNode(stmt)) {
                for (const decl of stmt.declarations) {
                    if (decl.init) {
                        this.scanNodeForIdentifiers(decl.init, identifiers);
                    }
                }
            }
            else if (NodeGuards_1.NodeGuards.isForStatementNode(stmt)) {
                if (stmt.init && !NodeGuards_1.NodeGuards.isVariableDeclarationNode(stmt.init)) {
                    this.scanNodeForIdentifiers(stmt.init, identifiers);
                }
                if (stmt.test) {
                    this.scanNodeForIdentifiers(stmt.test, identifiers);
                }
                if (stmt.update) {
                    this.scanNodeForIdentifiers(stmt.update, identifiers);
                }
            }
            else if (NodeGuards_1.NodeGuards.isWhileStatementNode(stmt) || NodeGuards_1.NodeGuards.isDoWhileStatementNode(stmt)) {
                this.scanNodeForIdentifiers(stmt.test, identifiers);
            }
            else if (NodeGuards_1.NodeGuards.isForOfStatementNode(stmt) || NodeGuards_1.NodeGuards.isForInStatementNode(stmt)) {
                this.scanNodeForIdentifiers(stmt.right, identifiers);
            }
            else if (stmt.type === 'SwitchStatement') {
                const switchStmt = stmt;
                this.scanNodeForIdentifiers(switchStmt.discriminant, identifiers);
                for (const switchCase of switchStmt.cases) {
                    if (switchCase.test) {
                        this.scanNodeForIdentifiers(switchCase.test, identifiers);
                    }
                }
            }
            else if (stmt.type === 'ThrowStatement') {
                const throwStmt = stmt;
                if (throwStmt.argument) {
                    this.scanNodeForIdentifiers(throwStmt.argument, identifiers);
                }
            }
        });
    }
};
exports.VMClosureAnalyzer = VMClosureAnalyzer;
exports.VMClosureAnalyzer = VMClosureAnalyzer = __decorate([
    (0, inversify_1.injectable)(),
    __param(0, (0, inversify_1.inject)(ServiceIdentifiers_1.ServiceIdentifiers.IVMCompilerUtils)),
    __param(1, (0, inversify_1.inject)(ServiceIdentifiers_1.ServiceIdentifiers.IVMVariableCollector)),
    __metadata("design:paramtypes", [typeof (_a = typeof IVMCompilerUtils_1.IVMCompilerUtils !== "undefined" && IVMCompilerUtils_1.IVMCompilerUtils) === "function" ? _a : Object, typeof (_b = typeof IVMVariableCollector_1.IVMVariableCollector !== "undefined" && IVMVariableCollector_1.IVMVariableCollector) === "function" ? _b : Object])
], VMClosureAnalyzer);
